import { Component, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { BehaviorSubject } from 'rxjs';

import { Team } from '../../../models/team/team.model';
import { User } from '../../../models/user.model';

import { AccountService } from '../../../services/account.service';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { TeamService } from '../../../services/team.service';
import { Utilities } from '../../../services/utilities';
import { DecisionBottomSheetComponent } from '../../bottom-sheet/decision-bottom-sheet/decision-bottom-sheet.component';
import { Roles, TeamType } from '../../../models/enums';
import { Router } from '@angular/router';
import { PACTHubService } from '../../../services/pacthub.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { ODPProjectWorkflowViewModel } from '../../../models/odpprojectworkflow/odpProjectWorkflow.model';

// TODO: Reference from config file
const TEAM_USER_LIMIT = 3;

@Component({
  selector: 'app-manage-teams',
  templateUrl: './manage-teams.component.html',
  styleUrls: ['./manage-teams.component.css'],
    imports: [
      CommonModule,
      FormsModule,
      MatButtonModule,
      MatFormFieldModule,
      MatListModule,
      MatSelectModule,
    ]
})
export class ManageTeamsComponent implements OnInit {
  isLoading$: BehaviorSubject<{ users: boolean, teams: boolean, labels: boolean }>;

  teams: Team[] = [];
  users: User[] = [];
  labels: string[] = [];

  teamLabel: string;
  teamType: TeamType;
  teamUserLimit: number;
  selectedUsers: User[];

  activeProject: ODPProjectWorkflowViewModel;
  activeProjectTitle: string;

  isOdpSupervisor: boolean = false;
  constructor(
    private accountService: AccountService,
    private alertService: AlertService,
    private teamService: TeamService,
    private bottomSheet: MatBottomSheet,
    private pactHub: PACTHubService,
    private router: Router
  ) { }

  ngOnInit() {
    this.teamUserLimit = TEAM_USER_LIMIT;
    this.teamType = this.teamService.getTeamTypeFromRole(this.accountService.GetActiveRole());
    this.selectedUsers = [];
    this.activeProject = this.accountService.GetActiveProject();
    this.activeProjectTitle = this.activeProject.projectTitle;
    this.getData();
    this.isOdpSupervisor = this.accountService.IsOdpSupervisorRole();
  }

  getData() {
    this.subscribeIsLoading();

    this.teamService.getInactiveUsers(this.teamType).subscribe({
      next: users => {
        this.users = users;
        this.isLoading$.value.users = false;
        this.isLoading$.next(this.isLoading$.value);
      },
      error: (error) => {
        this.isLoading$.error(error);
        this.showServerErrorMessage('Load Error', 'Unable to get users from the server.', error);
      }
    });
    this.teamService.getActiveTeams(this.teamType, this.activeProject.odpProjectWorkflowId).subscribe({
      next: teams => {
        console.log(teams);
        this.teams = teams;
        this.isLoading$.value.teams = false;
        this.isLoading$.next(this.isLoading$.value);
      },
      error: (error) => {
        this.isLoading$.error(error);
        this.showServerErrorMessage('Load Error', 'Unable to get teams from the server.', error);
      }
    });
    this.teamService.getTeamLabels(this.teamType, this.activeProject.odpProjectWorkflowId).subscribe({
      next: labels => {
        this.labels = labels;
        this.isLoading$.value.labels = false;
        this.isLoading$.next(this.isLoading$.value);
      },
      error: (error) => {
        this.isLoading$.error(error);
        this.showServerErrorMessage('Load Error', 'Unable to get team labels from the server.', error);
      }
    });
  }

  subscribeIsLoading() {
    this.isLoading$ = new BehaviorSubject({users: true, teams: true, labels: true});

    this.isLoading$.subscribe({
      next: ({users, teams, labels}) => {
        if (!users && !teams && !labels) {
          this.alertService.stopLoadingMessage();
          this.isLoading$.complete();
        } else if (!this.alertService.isLoadingInProgress) {
          this.alertService.startLoadingMessage();
        }
      },
      error: (_) => {
        // Stop loading message on server error.
        if (this.alertService.isLoadingInProgress) {
          this.alertService.stopLoadingMessage();
        }
      }
    });
  }

  showServerErrorMessage(caption: string, message: string, error?: any) {
    this.alertService.resetStickyMessage();
    this.alertService.showStickyMessage(caption,
      `${message}\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`, MessageSeverity.error, error);
  }

  handleSubmit() {
    if (!this.teamLabel) {
      this.alertService.showMessage('Team Manager', 'A team label must be selected to save a team.', MessageSeverity.error);
      return;
    }

    if (this.selectedUsers.length !== this.teamUserLimit) {
      this.alertService.showMessage('Team Manager', 'Three team members must be selected to save a team.', MessageSeverity.error);
      return;
    }

    this.teamService.addActiveTeam(this.selectedUsers, this.teamLabel, this.teamType, this.activeProject.odpProjectWorkflowId).subscribe({
      next: team => {
        team.canBeDeleted = true;
        this.teams = [...this.teams, team];

        //this.users = this.users.filter(user => !team.teamUsers.some(teamUser => user.id === teamUser.id));
        this.labels = this.labels.filter(label => label !== this.teamLabel);
        this.teamLabel = this.labels[0];

        for (var i = 0; i < this.selectedUsers.length; i++)
          this.pactHub.broadcastTeamsUpdated(this.selectedUsers[i].id);

        this.selectedUsers = [];
        this.alertService.showMessage('Team Manager', 'Team successfully added!', MessageSeverity.success);
      },
      error: (error) => this.showServerErrorMessage('Load Error', 'Unable to add team to the server.', error)
    });
  }

  editQueueClick($event, teamId: number) {
    $event.preventDefault();
    this.router.navigateByUrl('/manage/teams/queue/' + teamId.toString());
    return false;
  }

  handleDeleteTeam(team: Team) {
    if (!team.canBeDeleted) {
      this.alertService.showMessage("Cannot delete a team that still has items in its queue.", "", MessageSeverity.error);
    } else {
      this.bottomSheet.open(DecisionBottomSheetComponent, {
        data: {
          sheetTitle: 'Confirm delete',
          sheetText: 'Are you sure you want to delete this team?'
        },
        closeOnNavigation: true
      }).afterDismissed().subscribe(confirmed => {
        if (confirmed) {
          this.teamService.deleteTeam(team.id).subscribe({
            next: teamId => {
              this.labels.push(team.teamLabel);
              this.labels.sort();
              this.teamLabel = this.labels[0];

              for (var i = 0; i < team.teamUsers.length; i++)
                this.pactHub.broadcastTeamsUpdated(team.teamUsers[i].id);

              this.teams = this.teams.filter(t => t.id !== teamId);
              //this.users = [...this.users, ...team.teamUsers];
              this.users.sort();
              this.alertService.showMessage('Team Manager', 'Team has been removed!', MessageSeverity.success);
            },
            error: (error) => this.showServerErrorMessage('Server Error', 'Unable to delete team from the server.', error)
          });
        }
      });
    }
  }
}
